// Static explanatory text for rule types whose one-line template (built from
// rule_group_description/rule_definition_description, see SUBTYPE_DISPLAY in
// Rules.tsx) isn't self-explanatory on its own — currently just Arbitrage and
// +EV. Matched the same way RULE_OPTION_SELECTORS is: by the rule
// definition's group + description text, since rule_type_id isn't reliably
// known on the frontend.
export interface RuleDescriptionEntry {
  matches: (description: string) => boolean;
  blurb: string;
  // Filled with the saved threshold ({equality}/{value}) once the user has
  // set it. Shown alongside `blurb` on My Rules; not used on Create Rule,
  // since the value isn't chosen yet.
  template: string;
  // Overrides the "If the {ruleTargetName} is ..." wording on the Create
  // Rule threshold input, for when the backend's own definition text (just
  // "Arbitrage") isn't what the number actually measures.
  ruleTargetName?: string;
  // When set, the Create Rule threshold input skips the >=/<= picker and
  // always saves this comparison. Both Arbitrage and EV only ever make sense
  // as "at least X%" (a guaranteed edge), so it's fixed for both.
  fixedComparison?: string;
  // Suffix shown right after the value stepper on Create Rule (e.g. "%"), so
  // the user knows what unit they're targeting.
  valueSuffix?: string;
}

export const RULE_DESCRIPTIONS: RuleDescriptionEntry[] = [
  {
    matches: (description) => /\barb(itrage)?\b/i.test(description),
    blurb:
      "Arbitrage rules alert you when a guaranteed-profit opportunity appears for your selected sport/teams — betting both sides of a market at different sportsbooks locks in a profit regardless of the outcome.",
    template: "Alert when the arbitrage profit is {equality} {value}%",
    ruleTargetName: "Profit",
    fixedComparison: ">=",
    valueSuffix: "%",
  },
  {
    matches: (description) => /\+?\s*ev\b|expected value/i.test(description),
    blurb:
      "EV rules alert you when a bet is priced better than its true probability suggests for your selected sport/teams — giving you a mathematical edge over the long run.",
    template: "Alert when the expected value (EV) is {equality} {value}%",
    fixedComparison: ">=",
    valueSuffix: "%",
  },
];

export function findRuleDescription(
  description: string,
): RuleDescriptionEntry | undefined {
  return RULE_DESCRIPTIONS.find((r) => r.matches(description));
}
